//================================================================
//  Component: Final Asssessment Tab
//================================================================

//  Purpose: This contains the final assessment of the digital request

//  Properties:
//    - task = {An object, contains the digital request task details}
//    - setPageStatus = {A useState, used to determine the status of the page}

//  Example:
//    <FinalAssessmentTab
//      task={task}
//      setPageStatus={setPageStatus}
//    ></FinalAssessmentTab>    

// ==============================================================

// Libraries
import React, { useState, useContext, useReducer, useEffect } from 'react'
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router';

//Contexts
import { GetUser } from '../../../../Library/GlobalContexts';

// Components
import TotalContractValue from './Components/TotalContractValue';
import FinalApproval from './Components/FinalApproval';
import PublishToInventory from './Components/PublishToInventory';
import Tooltip from '../../../../Components/Tooltip/Tooltip';

//Functions
import QueryListener from '../../../../Library/QueryListener';
import WriteDocument from '../../../../Library/WriteDocument';

// Images
import Expand from '../../../../Components/Images/Icon_Collapse_Teal.svg';
import Collapse from '../../../../Components/Images/Icon_Expand_Teal.svg';

// CSS
import '../../Task.css'

export default function FinalAssessmentTab({
    task,
    setPageStatus
}) {

    //------------------------------------------------------
    //  useContext & React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const navigate = useNavigate()
    const routerUseLocation = useLocation();

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Returns empty array if this request is ready to be completed
    const [canSubmit, setCanSubmit] = useState([]);

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store whether a section is expanded
    const [taskSection, setTaskSection] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'totalContractValueCollapsed': false,
            'finalApprovalCollapsed': false,
            'publishToInventoryCollapsed': false
        }
    );


    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    function handleSubmit() {

        setPageStatus('pending');
    
        const updatedDoc = {
          'status': 'Completed',
          'lastmodifieddate': new Date(),
          'lastmodifiedby': {
            'email': getUser?.emailaddress,
            'givenname': getUser?.givenname,
            'surname': getUser?.surname
          },
          'activityfeed': task?.activityfeed,
          'stage': 'Completed',
        }
    
        // Update the activity feed
        updatedDoc.activityfeed.push({
          'activitydate': new Date(),
          'actionedby': {
            'email': getUser?.emailaddress,
            'givenname': getUser?.givenname,
            'surname': getUser?.surname
          },
          'action': 'completed',
          'comments': '',
        });
    
    
        // Write updated document to Firestore
        WriteDocument('requests', task?.requestid, updatedDoc, true)
          .then(() => {

            if (task?.requesttype === 'Decommission' && task?.dataretentionrequirements?.length > 0){

                return WriteDocument('applications', task?.applicationid, {'decommissiondata': {'dataretention': task?.dataretentionrequirements}}, true)
                    .then(() => {

                         setPageStatus('onload');
    
                        navigate(`/${routerUseLocation.pathname.split('/')[1]}/${routerUseLocation.pathname.split('/')[2]}?view=triage`);
                
                        const body = document.querySelector('.PageComponent-Container');

                        body.scrollIntoView({
                            behavior: 'smooth'
                        }, 300);

                    })
            }
    
            setPageStatus('onload');
    
            navigate(`/${routerUseLocation.pathname.split('/')[1]}/${routerUseLocation.pathname.split('/')[2]}?view=triage`);
    
            const body = document.querySelector('.PageComponent-Container');

            body.scrollIntoView({
                behavior: 'smooth'
            }, 300);

          })
          .catch((error) => {
    
            setPageStatus('error-fatal');
            console.log(error);
    
          });
    
      }

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Query Listener
    // Get all activity feeds from all related requests
    useEffect(() => {

        if (task === undefined) return;
        if (task?.parentrequestid === undefined) return;


        function onLoadChange(documents) {

            const blockersList = [];

            if (!task?.sentfinalapprovals) blockersList.push('Final Approvals have not been sent');
            if (documents.length > 0) blockersList.push('There are incomplete approvals/tasks in this request. See Activity Feed for remaining tasks');
            if (!['In Progress', 'Not Started'].includes(task?.status)) blockersList.push('This request has already been closed');

            setCanSubmit(blockersList);

        }

        function onError(error) {
            console.log(error);
            setPageStatus('error');
        }

        const unsubscribe = QueryListener('requests', [
            ['parentrequestid', '==', task?.parentrequestid],
            ['status', 'in', ['Not Started', 'In Progress', 'For Review']],
            ['tasktype', '!=', 'Digital Request']
        ], onLoadChange, onLoadChange, onError, ['createddate', 'asc']);

        return () => {
            unsubscribe();
        };

    // eslint-disable-next-line
    }, [task])


    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='flex flex-col gap-3 p-0'>

            {/* ===================================================== */}
            {/*  Total Contract Value                                 */}
            {/* ===================================================== */}

            <div className='Task-Section-Container'>

                <div className='flex flex-row gap-3 px-[2%] py-[18px] cursor-pointer' onClick={() => setTaskSection({ 'totalContractValueCollapsed': !taskSection.totalContractValueCollapsed })}>
                    <img
                        className='cursor-pointer'
                        src={taskSection?.totalContractValueCollapsed ? Collapse : Expand}
                        alt={'>'}
                    ></img>
                    <h4 className='text-[20px] m-0'>Total Contract Value</h4>
                </div>
                <hr className='m-0' hidden={taskSection?.totalContractValueCollapsed}></hr>

                {/* ------------- TOTAL CONTRACT VALUE ------------- */}
                <TotalContractValue
                    taskSection={taskSection}
                    task={task}
                ></TotalContractValue>

            </div>

            {/* ===================================================== */}
            {/*  Final Approval                                       */}
            {/* ===================================================== */}

            <div className='Task-Section-Container'>

                <div className='flex flex-row gap-3 px-[2%] py-[18px] cursor-pointer' onClick={() => setTaskSection({ 'finalApprovalCollapsed': !taskSection.finalApprovalCollapsed })}>
                    <img
                        className='cursor-pointer'
                        src={taskSection?.finalApprovalCollapsed ? Collapse : Expand}
                        alt={'>'}
                    ></img>
                    <h4 className='text-[20px] m-0'> Final Approval </h4>
                </div>
                <hr className='m-0' hidden={taskSection?.finalApprovalCollapsed}></hr>

                {/* ------------- FINAL APPROVAL TABLE ------------- */}
                <FinalApproval
                    taskSection={taskSection}
                    task={task}
                ></FinalApproval>

            </div>

            {/* ===================================================== */}
            {/*  Publish to Inventory                                 */}
            {/* ===================================================== */}

            <div className='Task-Section-Container' hidden={task?.requesttype !== 'New Initiative or Technology'}>

                <div className='flex flex-row gap-3 px-[2%] py-[18px] cursor-pointer' onClick={() => setTaskSection({ 'publishToInventoryCollapsed': !taskSection.publishToInventoryCollapsed })}>
                    <img
                        className='cursor-pointer'
                        src={taskSection?.publishToInventoryCollapsed ? Collapse : Expand}
                        alt={'>'}
                    ></img>
                    <h4 className='text-[20px] m-0'> Publish to Inventory </h4>
                </div>
                <hr className='m-0' hidden={taskSection?.publishToInventoryCollapsed}></hr>

                {/* ------------- PUBLISH TO INVENTORY FORM ------------- */}
                <PublishToInventory
                    taskSection={taskSection}
                    task={task}
                    setPageStatus={setPageStatus}
                    preventSubmit={canSubmit}
                ></PublishToInventory>

            </div>

            <br></br>

            {/* ===================================================== */}
            {/*  Complete Request Button                              */}
            {/* ===================================================== */}

            <div>
                <button
                    className='Primary-Button mb-3'
                    onClick={() => handleSubmit()}
                    hidden={task?.requesttype === 'New Initiative or Technology'}
                    disabled={canSubmit.length > 0} // Cannot complete request until all Final Approvals have been Approved
                >
                   {
                   canSubmit.length === 0 ?
                    'Complete Request' :
                    <Tooltip 
                            children={'Complete Request'} 
                            message={
                                <ul className='list-disc absolute rounded text-left top-0 left-0 py-2 pr-2 bg-gray-800'>
                                    {canSubmit.map((blocker, index) => (
                                        <li key={index}>{blocker}</li>
                                    ))}
                                </ul>
                            }
                        ></Tooltip>                    
                    }
                </button>
                <p className='font-light italic' hidden={task?.requesttype === 'New Initiative or Technology'}>
                    All final approvals must be secured prior to completing the request. Additionally, any outstanding requests in 'Diligence & Governance' must be fully addressed before advancing to the next stage.
                </p>
            </div>

        </div>
    );

}
